import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import {
  BatchJobChangePreview,
  BatchJobWorkflowActionKind,
  BatchJobWorkflowItem,
  buildJobChangePreview,
  formatBatchUserRef,
} from './batchJobWorkflow';

const useStyles = makeStyles(theme => ({
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
  changesList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

type BatchJobActionDialogProps = {
  open: boolean;
  action: BatchJobWorkflowActionKind;
  job?: BatchJobWorkflowItem;
  confirmed: boolean;
  submitting?: boolean;
  error?: string;
  onConfirmedChange: (confirmed: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
};

function actionCopy(action: BatchJobWorkflowActionKind) {
  if (action === 'approve') {
    return {
      title: 'Review and approve batch job',
      confirmLabel: 'Approve job',
      checkboxLabel:
        'I have reviewed the requested changes and approve opening this batch job for merge',
      intro:
        'Approving confirms the pull requests below match what you expect. After approval, the job can be merged.',
    };
  }
  return {
    title: 'Review and merge batch job',
    confirmLabel: 'Merge job',
    checkboxLabel:
      'I have reviewed the approved changes and want to merge all open pull requests for this job',
    intro:
      'Merging will attempt to merge every open pull request for this job. Failed merges stay open on GitHub for manual follow-up.',
  };
}

function ReviewSummary({ preview }: { preview: BatchJobChangePreview }) {
  const classes = useStyles();

  return (
    <>
      <Typography variant="subtitle1" gutterBottom>
        {preview.headline}
      </Typography>
      <Box className={classes.summaryGrid}>
        <Box className={classes.summaryCard}>
          <Typography variant="caption" color="textSecondary" display="block">
            Operation
          </Typography>
          <Typography variant="body2">{preview.operationLabel}</Typography>
        </Box>
        <Box className={classes.summaryCard}>
          <Typography variant="caption" color="textSecondary" display="block">
            Repositories
          </Typography>
          <Typography variant="body2">{preview.repositoryCount}</Typography>
        </Box>
        <Box className={classes.summaryCard}>
          <Typography variant="caption" color="textSecondary" display="block">
            Pull requests
          </Typography>
          <Typography variant="body2">{preview.pullRequestCount}</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        Requested changes
      </Typography>
      <List dense className={classes.changesList}>
        {preview.changes.map(change => (
          <ListItem key={change}>
            <ListItemText primary={change} />
          </ListItem>
        ))}
      </List>

      {preview.warnings?.map(warning => (
        <Box key={warning} mb={1}>
          <Alert severity="warning">{warning}</Alert>
        </Box>
      ))}
    </>
  );
}

export function BatchJobActionDialog({
  open,
  action,
  job,
  confirmed,
  submitting = false,
  error,
  onConfirmedChange,
  onClose,
  onConfirm,
}: BatchJobActionDialogProps) {
  const copy = actionCopy(action);
  const preview = job ? buildJobChangePreview(job) : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          {copy.intro}
        </Typography>

        {job ? (
          <Box mb={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            <Chip size="small" label={`Created by ${formatBatchUserRef(job.createdBy)}`} />
            {job.approvedBy ? (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Approved by ${formatBatchUserRef(job.approvedBy)}`}
              />
            ) : null}
            {job.mergedBy ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Merged by ${formatBatchUserRef(job.mergedBy)}`}
              />
            ) : null}
          </Box>
        ) : null}

        {error ? (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        {preview ? <ReviewSummary preview={preview} /> : null}

        <Divider />

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={confirmed}
                onChange={event => onConfirmedChange(event.target.checked)}
              />
            }
            label={copy.checkboxLabel}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!confirmed || submitting}
          onClick={onConfirm}
        >
          {submitting ? 'Working…' : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
